'use client';

import { useEffect, useRef, useState } from 'react';
import {
  computeAverageColor,
  computeDominantPalette,
  formatRgb,
  rgbToHex,
  type ColorSwatch,
  type Rgb,
} from '@/lib/browser-colortext-tools';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

// Downscale large uploads before sampling so the math stays fast.
const MAX_SAMPLE_EDGE = 240;

const SAMPLE_SRC = '/research-covers/how-many-photos-fit-in-your-phone-storage-capacity.jpg';

export default function BrowserImageAverageColorFinder() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [average, setAverage] = useState<Rgb | null>(null);
  const [palette, setPalette] = useState<ColorSwatch[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  // Landing on an empty drop zone with a panel that says "upload an image to see the result"
  // gives a visitor nothing to judge. Measured 2026-09-16: this tool bounced at 55.1% with 15
  // seconds a session, against 3.7% for instagram-story-generator, which shows a live result on
  // arrival. So the tool analyses one of our own research covers on mount and shows a real
  // result immediately; dropping a file replaces it.
  const [isSample, setIsSample] = useState(false);

  function reset() {
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
    setAverage(null);
    setPalette([]);
  }

  async function analyze(src: string) {
    const image = await loadImage(src);
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, MAX_SAMPLE_EDGE / Math.max(image.width, image.height));
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      throw new Error('This browser blocked the canvas needed to read the image.');
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    setAverage(computeAverageColor(data));
    setPalette(computeDominantPalette(data, 6));
    setPreviewUrl(src);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await analyze(SAMPLE_SRC);
        if (!cancelled) setIsSample(true);
      } catch {
        // A missing sample must never break the tool; the drop zone still works.
      }
    })();
    return () => {
      cancelled = true;
    };
    // Runs once, on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(file: File | null) {
    setErrorMessage(null);
    setCopyMessage(null);
    setIsSample(false);
    reset();

    if (!file) {
      setFileName(null);
      return;
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Choose an image file.');
      return;
    }

    setFileName(file.name);
    const objectUrl = URL.createObjectURL(file);

    try {
      await analyze(objectUrl);
      // Fires only on a real drop, never on the mount-time sample: the sample already loads
      // automatically, and counting that as "used" would track every page view, not real use.
      void consumeToolUsage('image-average-color-finder', '').catch(() => {});
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      setFileName(null);
      setErrorMessage(error instanceof Error ? error.message : 'The image could not be analyzed.');
    }
  }

  async function handleCopy(value: string) {
    try {
      await copyText(value);
      setCopyMessage(`Copied ${value}.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  const averageHex = average ? rgbToHex(average) : null;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Color</span>
        <h2 className="tool-live-title">Image average color finder</h2>
        <p className="tool-live-subtitle">
          Upload an image to read its average color and a small dominant-color palette, then copy
          any hex or RGB value.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFile(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{fileName ?? 'Choose an image or drop it here'}</strong>
              <span>JPG, PNG, WebP and GIF, nothing leaves your device</span>
            </div>
          </button>
          <input
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFile(event.target.files?.[0] ?? null)}
            ref={inputRef}
            style={{ display: 'none' }}
            type="file"
          />

          {previewUrl ? (
            <div className="tool-live-preview" style={{ marginTop: '1rem' }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt="Uploaded preview"
                src={previewUrl}
                style={{ maxWidth: '100%', borderRadius: '0.5rem' }}
              />
            </div>
          ) : null}

          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {errorMessage ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {errorMessage}
            </p>
          ) : null}
        </section>

        <section className="tool-live-preview">
          {average && averageHex ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: '4rem',
                    height: '4rem',
                    borderRadius: '0.75rem',
                    background: averageHex,
                    border: '1px solid rgba(148, 163, 184, 0.35)',
                    flexShrink: 0,
                  }}
                />
                <div style={{ display: 'grid', gap: '0.35rem' }}>
                  <strong>Average color</strong>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      className="button button-secondary"
                      onClick={() => void handleCopy(averageHex)}
                      type="button"
                    >
                      {averageHex}
                    </button>
                    <button
                      className="button button-secondary"
                      onClick={() => void handleCopy(formatRgb(average))}
                      type="button"
                    >
                      {formatRgb(average)}
                    </button>
                  </div>
                </div>
              </div>

              <strong>Dominant palette</strong>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(7rem, 1fr))',
                  gap: '0.75rem',
                  marginTop: '0.5rem',
                }}
              >
                {palette.map((swatch) => (
                  <button
                    className="button button-secondary"
                    key={swatch.hex}
                    onClick={() => void handleCopy(swatch.hex)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: '0.35rem',
                      padding: '0.5rem',
                      height: 'auto',
                    }}
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'block',
                        height: '2.5rem',
                        borderRadius: '0.4rem',
                        background: swatch.hex,
                        border: '1px solid rgba(148, 163, 184, 0.35)',
                      }}
                    />
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {swatch.hex}
                    </span>
                    <span style={{ fontSize: '0.72rem', opacity: 0.7 }}>
                      {(swatch.share * 100).toFixed(1)}%
                    </span>
                  </button>
                ))}
              </div>
              <p className="tool-live-panel-note">
                {isSample
                  ? 'This is one of our own research covers, shown so you can see what the tool returns. Drop your own image to replace it.'
                  : 'Tap any swatch to copy its hex.'}
              </p>
            </>
          ) : (
            <p className="tool-live-panel-note">
              Upload an image to see its average color and dominant palette.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The image could not be opened.'));
    image.src = src;
  });
}
